import React, { useRef, useMemo, useCallback } from "react";
import { Line } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import { Chart as ChartJS, registerables } from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import "./InstrumentCharts.css";
import { createCrosshairPlugin } from "../../shared/util/createCrosshairPlugin";
import {
  buildSignalMarkers,
  chartOptions,
  signalMarkersPlugin,
  toPoints,
} from "../../shared/util/instrumentCharts";

ChartJS.register(...registerables, zoomPlugin, annotationPlugin);

// ─── Component ────────────────────────────────────────────────────────────────
// data: the response of /api/analytics/:ticker, one value per day of
// data.dates. rangeValues: [first, last] day index shown.
const InstrumentChartsPerformance = ({ data, rangeValues }) => {
  // PNL in USD (profitCurrency), or in the quote currency without a USD rate
  const { dates, NOTIFICATION, PNL, TAS, TRADE, profitCurrency } = data;

  // Chart refs
  const pnlChartRef = useRef(null);
  const tasChartRef = useRef(null);
  const tradeChartRef = useRef(null);

  const allChartRefs = useMemo(() => [pnlChartRef, tasChartRef, tradeChartRef], []);

  // Crosshair
  const cursorPositionRef = useRef({ index: null, y: null, currentChartId: null });
  const crosshairPlugin = useMemo(() => createCrosshairPlugin(cursorPositionRef), []);
  const plugins = useMemo(() => [signalMarkersPlugin, crosshairPlugin], [crosshairPlugin]);

  // Mouse handlers
  const handleChartMouseMove = useCallback((event, chart) => {
    const rect = chart.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const index = Math.round(chart.scales.x.getValueForPixel(x));
    const y = event.clientY - rect.top;

    cursorPositionRef.current.index = index;
    cursorPositionRef.current.y = y;
    cursorPositionRef.current.currentChartId = chart.id;

    allChartRefs.forEach((ref) => { if (ref.current) ref.current.draw(); });
  }, [allChartRefs]);

  const handleChartMouseOut = useCallback(() => {
    cursorPositionRef.current.index = null;
    cursorPositionRef.current.y = null;
    cursorPositionRef.current.currentChartId = null;

    allChartRefs.forEach((ref) => { if (ref.current) ref.current.draw(); });
  }, [allChartRefs]);

  // ─── Memoized chart data ───────────────────────────────────────────────────
  const pnlData = useMemo(() => ({
    datasets: [{
      label: "Profit & Loss",
      data: toPoints(PNL),
      fill: true, backgroundColor: "rgba(75,192,192,0.2)",
      borderColor: "rgba(50,128,128,1)", borderWidth: 0.8, pointRadius: 0,
    }],
  }), [PNL]);

  const tasData = useMemo(() => ({
    datasets: [{
      label: "TA Score",
      data: toPoints(TAS),
      fill: true, backgroundColor: "rgba(75,192,192,0.2)",
      borderColor: "rgba(50,128,128,1)", borderWidth: 0.8, pointRadius: 0,
    }],
  }), [TAS]);

  const tradeData = useMemo(() => ({
    datasets: [{
      label: "TRADE Signal",
      data: toPoints(TRADE),
      fill: true, backgroundColor: "rgba(75,192,192,0.2)",
      borderColor: "rgba(50,128,128,1)", borderWidth: 0.8, pointRadius: 0,
    }],
  }), [TRADE]);

  // ─── Memoized signals: buys and sells, without the RE-BUYs ─────────────────
  const signals = useMemo(() => {
    const { lines, spans } = buildSignalMarkers(NOTIFICATION);
    return { lines, spans };
  }, [NOTIFICATION]);

  // ─── Memoized chart options ────────────────────────────────────────────────
  const pnlOptions = useMemo(
    () => chartOptions({ title: `Compounded Profit${profitCurrency ? ` in ${profitCurrency}` : ""} [%]`, range: rangeValues, dates, signals }),
    [rangeValues, dates, signals, profitCurrency]
  );

  const tasOptions = useMemo(
    () => chartOptions({ title: "TA Score [-]", range: rangeValues, dates, signals }),
    [rangeValues, dates, signals]
  );

  const tradeOptions = useMemo(
    () => chartOptions({ title: "TRADE [-]", range: rangeValues, dates, signals, showDates: true }),
    [rangeValues, dates, signals]
  );

  return (
    <div>
      <div className="chart-container">
        <div className="chart-wrapper">
          <div className="pnl-chart">
            <Line
              ref={pnlChartRef}
              data={pnlData}
              options={pnlOptions}
              onMouseMove={(e) => handleChartMouseMove(e.nativeEvent, pnlChartRef.current)}
              onMouseOut={handleChartMouseOut}
              plugins={plugins}
            />
          </div>
          <div className="tas-chart">
            <Line
              ref={tasChartRef}
              data={tasData}
              options={tasOptions}
              onMouseMove={(e) => handleChartMouseMove(e.nativeEvent, tasChartRef.current)}
              onMouseOut={handleChartMouseOut}
              plugins={plugins}
            />
          </div>
          <div className="trade-chart">
            <Line
              ref={tradeChartRef}
              data={tradeData}
              options={tradeOptions}
              onMouseMove={(e) => handleChartMouseMove(e.nativeEvent, tradeChartRef.current)}
              onMouseOut={handleChartMouseOut}
              plugins={plugins}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstrumentChartsPerformance;
